import { Outlet, useNavigate, useParams } from 'react-router-dom'

import { Box, Button as GammaButton, Text } from '@factorialco/deprecated-design-system'
import {
  ProjectionKind,
  useFundaeFinishGroupDatatable,
} from 'generated/resources/datatable.graphql'

import type { TrainingsTrainingClass } from 'modules/trainings/pages/Trainings/Revamp/TrainingDetail/Detail/types'

import DataTable from 'modules/core/components/DataTable'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import UserAvatar from 'modules/core/components/UserAvatar'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { EducationLevelCell, NifCell, PhoneCell, ProfessionalCategoryCell } from './cells'
import { missingDataFilter } from './filters/missingDataFilter'
import { PermissionCallout } from './permissions-callout'

export function Datatable({
  trainingClass,
  validate,
}: {
  trainingClass: TrainingsTrainingClass
  validate: boolean
}) {
  const { search, onSearch } = useDebouncedSearch()
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const { id } = useParams()

  const source = useFundaeFinishGroupDatatable(
    [
      {
        identifier: 'avatar',
        kind: ProjectionKind.Custom,
        title: i18n.t(
          'trainings.trainings_detail.fundae.participants_datatable.columns.participant'
        ),
        template: '',
        render: ({ access }) => (
          <Box flexDirection="row" gap="s8" alignItems="center">
            <UserAvatar
              user={{
                fullName: access?.fullName || '',
                avatar: access?.avatar || '',
              }}
              size="s24"
            />
            <Text size="300" ellipsis>
              {access?.fullName}
            </Text>
          </Box>
        ),
      },
      {
        identifier: 'identifier',
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.trainings_detail.fundae.participants_datatable.columns.nif'),
        template: '',
        render: (item) => <NifCell {...item} validate={validate} />,
      },
      {
        identifier: 'email',
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.trainings_detail.fundae.participants_datatable.columns.email'),
        hidden: true,
        template: '',
        render: ({ access }) => (
          <Text size="200" ellipsis>
            {access?.employee?.email}
          </Text>
        ),
      },
      {
        identifier: 'phone',
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.trainings_detail.fundae.participants_datatable.columns.phone'),
        template: '',
        render: (item) => <PhoneCell {...item} validate={validate} />,
      },
      {
        identifier: 'professional_category',
        kind: ProjectionKind.Custom,
        title: i18n.t(
          'trainings.trainings_detail.fundae.participants_datatable.columns.professional_category'
        ),
        template: '',
        render: (item) => (
          <ProfessionalCategoryCell {...item} validate={validate} trainingId={id} />
        ),
      },
      {
        identifier: 'education_level',
        kind: ProjectionKind.Custom,
        title: i18n.t(
          'trainings.trainings_detail.fundae.participants_datatable.columns.education_level'
        ),
        template: '',
        render: (item) => <EducationLevelCell {...item} validate={validate} trainingId={id} />,
      },
      {
        identifier: 'certificate',
        kind: ProjectionKind.Custom,
        title: i18n.t('trainings.trainings_detail.fundae.participants_datatable.columns.diploma'),
        hidden: true,
        template: '',
        render: ({ certificate }) => (
          <Text size="300">{certificate ? i18n.t('common.yes') : i18n.t('common.no')}</Text>
        ),
      },
    ],
    {
      variables: { trainingClassId: Number(trainingClass.id), search: search },
      defaultSort: undefined,
    }
  )

  return (
    <Box gap="s16">
      <Text size="300" color="textPrimary" weight="semibold">
        {i18n.t('trainings.trainings_detail.fundae.participants_datatable.title')}
      </Text>
      <DataTable.Provider source={source} filters={[missingDataFilter({ source })]}>
        <Box gap="s16">
          <PermissionCallout />
          <DataTable.HeadBar
            disableExport
            search={{
              placeholder: i18n.t('common.search'),
              value: search,
              onChange: onSearch,
            }}
            actions={
              <>
                <PopoverMenu type="list">
                  <PopoverMenu.Trigger>
                    <GammaButton type="secondary">
                      {i18n.t(
                        'trainings.trainings_detail.fundae.participants_datatable.match_data_button.title'
                      )}
                    </GammaButton>
                  </PopoverMenu.Trigger>
                  <PopoverMenu.Content side="bottom" align="end">
                    <PopoverMenu.TouchableOption
                      onClick={() => {
                        track(
                          trackingEvents.training.trainingDetail.fundae.finishGroupsExport
                            .matchData,
                          {
                            selected_mapping: 'education_level',
                          }
                        )
                        navigate(
                          navigation.trainings.list.details_revamp.view.fundae.educationLevel.fullPath(
                            {
                              id: String(id),
                            }
                          )
                        )
                      }}
                      title={i18n.t(
                        'trainings.trainings_detail.fundae.participants_datatable.match_data_button.education_level'
                      )}
                    />

                    <PopoverMenu.TouchableOption
                      onClick={() => {
                        track(
                          trackingEvents.training.trainingDetail.fundae.finishGroupsExport
                            .matchData,
                          {
                            selected_mapping: 'professional_category',
                          }
                        )
                        navigate(
                          navigation.trainings.list.details_revamp.view.fundae.professionalCategory.fullPath(
                            {
                              id: String(id),
                            }
                          )
                        )
                      }}
                      title={i18n.t(
                        'trainings.trainings_detail.fundae.participants_datatable.match_data_button.professional_category'
                      )}
                    />
                  </PopoverMenu.Content>
                </PopoverMenu>
              </>
            }
          />
          <DataTable.Table />
          <Outlet />
        </Box>
      </DataTable.Provider>
    </Box>
  )
}
